/**
 * External / guest file-access Graph calls (C13).
 *
 * Two reads, behind the graph/ layer, delegated + zero-egress:
 *   • the tenant guest roster (/users?$filter=userType eq 'Guest')
 *   • per-file permissions on a site's document library, filtered to external
 *     grantees — the bounded, best-effort drill-down.
 */
import { graphBatch, graphGet, graphGetAll, graphGetPages } from '@/graph/client';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import type { SiteRef } from '@/graph/modules/sites';

const SCOPES = CAPABILITY_SCOPES.C13.scopes;

export const GUEST_ENDPOINTS = {
  users: '/users',
  drivePermissions: '/drives/{driveId}/items/{itemId}/permissions',
} as const;

export interface GuestUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
}

export interface ExternalGrant {
  siteId: string;
  siteName: string;
  fileId: string;
  fileName: string;
  webUrl: string;
  role: string;
  granteeId: string | null;
  granteeName: string;
  granteeEmail: string | null;
  kind: 'direct' | 'link' | 'invitation';
}

interface PermUser {
  id?: string;
  displayName?: string;
  email?: string;
}
interface Permission {
  roles?: string[];
  grantedToV2?: { user?: PermUser };
  grantedToIdentitiesV2?: { user?: PermUser }[];
  link?: { scope?: string; type?: string };
  invitation?: { email?: string };
}

/** All guest (external) users in the tenant. */
export async function getGuestUsers(): Promise<GuestUser[]> {
  const rows = await graphGetAll<{
    id: string;
    displayName?: string;
    userPrincipalName?: string;
    mail?: string | null;
  }>(GUEST_ENDPOINTS.users, {
    scopes: SCOPES,
    query: {
      $filter: "userType eq 'Guest'",
      $select: 'id,displayName,userPrincipalName,mail',
      $top: '999',
    },
  });
  return rows.map((u) => ({
    id: u.id,
    displayName: u.displayName ?? u.userPrincipalName ?? u.id,
    userPrincipalName: u.userPrincipalName ?? '',
    mail: u.mail ?? null,
  }));
}

function isExternal(u: PermUser, guestIds: ReadonlySet<string>, guestEmails: ReadonlySet<string>): boolean {
  if (u.id && guestIds.has(u.id)) return true;
  if (u.email && guestEmails.has(u.email.toLowerCase())) return true;
  return /#ext#/i.test(u.email ?? '') || /#ext#/i.test(u.displayName ?? '');
}

/**
 * Scan one site's default document library (bounded) and return the file
 * permissions granted to external identities. Best-effort throughout.
 */
export async function getSiteFileGrants(
  site: SiteRef,
  guestIds: ReadonlySet<string>,
  guestEmails: ReadonlySet<string>,
  maxFiles: number,
): Promise<ExternalGrant[]> {
  let driveId: string | undefined;
  try {
    const d = await graphGet<{ id?: string }>(`/sites/${encodeURIComponent(site.id)}/drive`, {
      scopes: SCOPES,
      query: { $select: 'id' },
    });
    driveId = d.id;
  } catch {
    return [];
  }
  if (!driveId) return [];

  const files: { id: string; name: string; webUrl: string }[] = [];
  try {
    for await (const page of graphGetPages<{
      id?: string;
      name?: string;
      webUrl?: string;
      file?: unknown;
      folder?: unknown;
    }>(`/drives/${driveId}/root/children`, {
      scopes: SCOPES,
      query: { $select: 'id,name,webUrl,file,folder', $top: '200' },
    })) {
      for (const it of page) {
        if (it.folder || !it.file || !it.id) continue;
        files.push({ id: it.id, name: it.name ?? it.id, webUrl: it.webUrl ?? '' });
        if (files.length >= maxFiles) break;
      }
      if (files.length >= maxFiles) break;
    }
  } catch {
    // library not readable — return nothing for this site
  }
  if (files.length === 0) return [];

  const grants: ExternalGrant[] = [];
  const CHUNK = 20;
  for (let i = 0; i < files.length; i += CHUNK) {
    const chunk = files.slice(i, i + CHUNK);
    const responses = await graphBatch<{ value?: Permission[] }>(
      chunk.map((f) => ({
        id: f.id,
        method: 'GET' as const,
        url: `/drives/${driveId}/items/${f.id}/permissions`,
      })),
      { scopes: SCOPES },
    ).catch(() => null);
    if (!responses) continue;
    for (const resp of responses) {
      if (resp.status < 200 || resp.status >= 300) continue;
      const file = chunk.find((f) => f.id === resp.id);
      if (!file) continue;
      for (const p of resp.body?.value ?? []) {
        const role = (p.roles ?? []).join('/') || 'read';
        const candidates: { u: PermUser; kind: 'direct' | 'link' }[] = [];
        if (p.grantedToV2?.user) candidates.push({ u: p.grantedToV2.user, kind: 'direct' });
        for (const g of p.grantedToIdentitiesV2 ?? []) {
          if (g.user) candidates.push({ u: g.user, kind: 'link' });
        }
        for (const { u, kind } of candidates) {
          if (!isExternal(u, guestIds, guestEmails)) continue;
          grants.push({
            siteId: site.id,
            siteName: site.displayName,
            fileId: file.id,
            fileName: file.name,
            webUrl: file.webUrl,
            role,
            granteeId: u.id ?? null,
            granteeName: u.displayName ?? u.email ?? u.id ?? 'external user',
            granteeEmail: u.email ?? null,
            kind,
          });
        }
        if (p.invitation?.email) {
          grants.push({
            siteId: site.id,
            siteName: site.displayName,
            fileId: file.id,
            fileName: file.name,
            webUrl: file.webUrl,
            role,
            granteeId: null,
            granteeName: p.invitation.email,
            granteeEmail: p.invitation.email,
            kind: 'invitation',
          });
        }
      }
    }
  }
  return grants;
}
